/**
 * i18n setup. Vietnamese is the default; English is the alternative.
 *
 * The dictionaries are plain typed objects rather than JSON so `en` is structurally checked
 * against `vi` at compile time — a missing key is a build error, not a runtime "key.name".
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { en } from './locales/en';
import { vi } from './locales/vi';

export const SUPPORTED_LOCALES = ['vi', 'en'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'vi';
const STORAGE_KEY = 'vgp_locale';

export function getStoredLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'vi' || stored === 'en') return stored;
  } catch {
    /* private mode — fall through to the default */
  }
  // Vietnamese is the product's default, not a guess from the browser: this is a
  // Vietnamese-first product and an en-US browser should still open in Vietnamese
  // until the user chooses otherwise in Account → Profile.
  return DEFAULT_LOCALE;
}

export function setStoredLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* private mode */
  }
  void i18n.changeLanguage(locale);
  document.documentElement.setAttribute('lang', locale);
}

void i18n.use(initReactI18next).init({
  resources: {
    vi: { translation: vi },
    en: { translation: en },
  },
  lng: getStoredLocale(),
  fallbackLng: DEFAULT_LOCALE,
  interpolation: { escapeValue: false }, // React escapes for us
  returnNull: false,
});

document.documentElement.setAttribute('lang', i18n.language);

export default i18n;
